import { useCallback } from "react";
import { useEvidenceSelection } from "./useEvidenceSelection";

/**
 * Hook to manage evidence drawer UI state (open/close)
 * Drawer state is derived from URL (if evidenceId/requestId exists, drawer is open)
 * This ensures all instances of this hook share the same state
 * Uses useEvidenceSelection for evidence data and URL management
 */
export const useEvidenceDrawer = () => {
  // Get evidence selection from URL (this handles URL params)
  const {
    evidenceId,
    requestId,
    chosenEvidence,
    isLoadingEvidence,
    isFromUrlNavigation,
    getAllEvidences,
    selectEvidenceById,
    clearSelection,
  } = useEvidenceSelection();
  
  // Derive drawer state from URL - drawer is open if evidence is selected
  const isDrawerOpen = Boolean(evidenceId || requestId);
  
  // Open drawer by selecting evidence in URL
  const openDrawer = useCallback((evidence?: any) => {
    // Select evidence in URL (this opens the drawer)
    if (evidence?._id) {
      const fromSearch = evidence.status === 'search';
      selectEvidenceById(evidence._id, fromSearch);
    }
  }, [selectEvidenceById]);

  // Close drawer by clearing evidence selection from URL
  const closeDrawer = useCallback(() => {
    clearSelection();
  }, [clearSelection]);

  // Toggle drawer
  const toggleDrawer = useCallback((evidence?: any) => {
    if (isDrawerOpen) {
      closeDrawer();
    } else {
      openDrawer(evidence);
    }
  }, [isDrawerOpen, openDrawer, closeDrawer]);

  // Set chosen evidence by ID (selects in URL which opens/closes drawer)
  const setChosenEvidenceById = useCallback((evidenceId: string | null, fromSearch?: boolean) => {
    if (evidenceId) {
      selectEvidenceById(evidenceId, fromSearch);
    } else {
      clearSelection();
    }
  }, [selectEvidenceById, clearSelection]);

  // Choose match function that finds evidence and opens drawer
  const chooseMatch = useCallback((id: string, fromSearch?: boolean) => {
    const allEvidences = getAllEvidences();

    let foundEvidence = null;
    
    if (allEvidences) {
      foundEvidence = allEvidences.find((evidence: any) => evidence._id === id);
    }

    if (foundEvidence) {
      if (fromSearch) {
        foundEvidence = { ...foundEvidence, status: "search" };
      } 
      openDrawer(foundEvidence);
    } else {
      // If evidence not found, just set the ID in URL and open drawer
      setChosenEvidenceById(id, fromSearch);
    }
  }, [getAllEvidences, openDrawer, setChosenEvidenceById]);

  return {
    // State
    isOpen: isDrawerOpen,
    chosenEvidence,
    evidenceId,
    requestId,
    isLoadingEvidence,
    isFromUrlNavigation,
    
    // Actions
    openDrawer,
    closeDrawer,
    toggleDrawer,
    setChosenEvidenceById,
    chooseMatch,
    
    // Legacy compatibility
    opened: isDrawerOpen,
    open: openDrawer,
    close: closeDrawer,
    setChosenEvidence: (evidence: any | null) => {
      if (evidence?._id) {
        setChosenEvidenceById(evidence._id, evidence.status === 'search');
      } else {
        setChosenEvidenceById(null);
      }
    },
  };
};
